/**
 * Compare contracts to find conflicting fields using revision history
 * @param {Object} localContract - The stored local contract
 * @param {Object} serverContract - The latest server contract
 * @param {Object} pendingChanges - The pending changes to be applied
 * @returns {Array} Array of conflict objects
 */
export function findContractConflicts(localContract, serverContract, pendingChanges) {
	const conflicts = []

	// Get revisions that occurred after our local version
	const newRevisions = (serverContract?.revisions || []).filter(
		(revision) => revision.version > (localContract.version || 0)
	)

	// Group revisions by field for easier conflict detection
	const revisionsByField = newRevisions.reduce((acc, revision) => {
		const key = revision.item_id
			? `${revision.field_name}[${revision.item_id}]`
			: revision.field_name

		if (!acc[key]) {
			acc[key] = []
		}
		acc[key].push(revision)
		return acc
	}, {})

	// Helper to normalize values for comparison
	const normalizeValue = (value) => {
		if (value === undefined || value === null || value === '') {
			return null
		}
		return value
	}

	// Helper to compare values
	const areValuesEqual = (val1, val2) => {
		const norm1 = normalizeValue(val1)
		const norm2 = normalizeValue(val2)

		// If both are null/empty, they're equal
		if (norm1 === null && norm2 === null) {
			return true
		}

		// Use JSON.stringify for object comparison
		return JSON.stringify(norm1) === JSON.stringify(norm2)
	}

	// Check each pending change against revisions
	Object.keys(pendingChanges).forEach((field) => {
		// Skip version field
		if (field === 'version') return

		if (field === 'inventory_list' && pendingChanges[field]?.items) {
			pendingChanges[field].items.forEach((item, index) => {
				const itemKey = `inventory_list[${item.id || index}]`
				if (revisionsByField[itemKey]) {
					const lastRevision = revisionsByField[itemKey][revisionsByField[itemKey].length - 1]
					const serverItem = lastRevision ? JSON.parse(lastRevision.new_value) : null

					// Only add conflict if values are actually different
					if (!areValuesEqual(serverItem, item)) {
						conflicts.push({
							field: itemKey,
							serverValue: serverItem,
							localValue: item,
							revisions: revisionsByField[itemKey],
							serverContract: serverContract
						})
					}
				}
			})

			// Also check for items that exist on server but not in pending changes
			Object.keys(revisionsByField).forEach((key) => {
				if (key.startsWith('inventory_list[')) {
					const itemId = key.match(/\[(.*?)\]/)?.[1]
					if (
						itemId &&
						!pendingChanges[field].items.find((item) => (item.id || '').toString() === itemId)
					) {
						const lastRevision = revisionsByField[key][revisionsByField[key].length - 1]
						const serverItem = lastRevision ? JSON.parse(lastRevision.new_value) : null
						if (serverItem) {
							conflicts.push({
								field: key,
								serverValue: serverItem,
								localValue: null,
								revisions: revisionsByField[key],
								serverContract: serverContract
							})
						}
					}
				}
			})
		} else if (field === 'expenses' && Array.isArray(pendingChanges[field])) {
			pendingChanges[field].forEach((expense) => {
				const expenseKey = `expenses[${expense.id || expense.type}]`
				if (revisionsByField[expenseKey]) {
					const lastRevision = revisionsByField[expenseKey][revisionsByField[expenseKey].length - 1]
					const serverExpense = lastRevision ? JSON.parse(lastRevision.new_value) : null

					// Only add conflict if values are actually different
					if (!areValuesEqual(serverExpense, expense)) {
						conflicts.push({
							field: expenseKey,
							serverValue: serverExpense,
							localValue: expense,
							revisions: revisionsByField[expenseKey],
							serverContract: serverContract
						})
					}
				}
			})

			// Also check for expenses that exist on server but not in pending changes
			Object.keys(revisionsByField).forEach((key) => {
				if (key.startsWith('expenses[')) {
					const expenseId = key.match(/\[(.*?)\]/)?.[1]
					if (
						expenseId &&
						!pendingChanges[field].find(
							(expense) => (expense.id || expense.type || '').toString() === expenseId
						)
					) {
						const lastRevision = revisionsByField[key][revisionsByField[key].length - 1]
						const serverExpense = lastRevision ? JSON.parse(lastRevision.new_value) : null
						if (serverExpense) {
							conflicts.push({
								field: key,
								serverValue: serverExpense,
								localValue: null,
								revisions: revisionsByField[key],
								serverContract: serverContract
							})
						}
					}
				}
			})
		} else {
			// Handle regular fields
			if (revisionsByField[field]) {
				const lastRevision = revisionsByField[field][revisionsByField[field].length - 1]
				const serverValue = JSON.parse(lastRevision.new_value)?.value
				const pendingValue = pendingChanges[field]

				// Only add conflict if values are actually different
				if (!areValuesEqual(serverValue, pendingValue)) {
					conflicts.push({
						field,
						serverValue,
						localValue: pendingValue,
						revisions: revisionsByField[field],
						serverContract: serverContract
					})
				}
			}
		}
	})

	return conflicts
}
